import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '../ui/tooltip';
import Status from '../general-components/Status';
import { cn } from '../../lib/utils';
import { ImplantMember } from '../../services/api/folderService';
import { ImplantBrief } from './ImplantCatalogPicker';

interface SelectedImplantsListProps {
    existingImplants: ImplantMember[];
    pendingImplants: ImplantBrief[];
    pendingRemoveId: string | null;
    onRemove: (id: string) => void;
    onUndoPending: (id: string) => void;
}

interface ImplantRowFields {
    _id: string;
    Model?: string;
    Type?: string;
    'MRI-status'?: number;
}

interface ImplantRowProps {
    implant: ImplantRowFields;
    variant: 'existing' | 'pending';
    actionLabel: string;
    onAction: (id: string) => void;
    disabled?: boolean;
}

const ImplantRow = ({
    implant,
    variant,
    actionLabel,
    onAction,
    disabled,
}: ImplantRowProps) => {
    const { t } = useTranslation();
    const model = implant.Model ?? t('folders.manageImplants.unnamed');
    return (
        <li
            className={cn(
                'flex items-center gap-3 rounded-lg border border-subtle bg-card px-3 py-2',
                variant === 'pending' && 'border-dashed'
            )}
        >
            <div className="h-6 w-6 flex-shrink-0">
                <Status status={implant['MRI-status'] ?? 0} small />
            </div>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                            {model}
                        </div>
                        {implant.Type && (
                            <div className="truncate text-xs text-subtle-foreground">
                                {implant.Type}
                            </div>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    align="start"
                    className="max-w-xs"
                >
                    <p className="font-medium">{model}</p>
                    {implant.Type && <p className="text-xs">{implant.Type}</p>}
                </TooltipContent>
            </Tooltip>
            <Button
                variant="outline"
                size="sm"
                className={
                    variant === 'existing' ? 'hover:text-destructive' : undefined
                }
                disabled={disabled}
                onClick={() => onAction(implant._id)}
            >
                <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                {actionLabel}
            </Button>
        </li>
    );
};

export const SelectedImplantsList = ({
    existingImplants,
    pendingImplants,
    pendingRemoveId,
    onRemove,
    onUndoPending,
}: SelectedImplantsListProps) => {
    const { t } = useTranslation();
    const headerLabel = (() => {
        const e = existingImplants.length;
        const p = pendingImplants.length;
        if (e === 0 && p === 0) return t('folders.manageImplants.nothingYet');
        const parts: string[] = [];
        if (e > 0)
            parts.push(t('folders.manageImplants.inFolder', { count: e }));
        if (p > 0)
            parts.push(t('folders.manageImplants.pending', { count: p }));
        return parts.join(' · ');
    })();

    const isEmpty =
        existingImplants.length === 0 && pendingImplants.length === 0;

    return (
        <aside className="flex flex-col md:w-2/5 md:min-w-0 md:overflow-hidden">
            <div className="mb-2 flex-shrink-0 text-sm font-medium text-foreground">
                {headerLabel}
            </div>
            <ul className="flex-1 space-y-2 overflow-y-auto md:min-h-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {existingImplants.map((implant) => {
                    const isRemoving = pendingRemoveId === implant._id;
                    return (
                        <ImplantRow
                            key={implant._id}
                            implant={implant}
                            variant="existing"
                            actionLabel={
                                isRemoving
                                    ? t('folders.manageImplants.removing')
                                    : t('folders.manageImplants.remove')
                            }
                            onAction={onRemove}
                            disabled={isRemoving}
                        />
                    );
                })}
                {pendingImplants.map((implant) => (
                    <ImplantRow
                        key={implant._id}
                        implant={implant}
                        variant="pending"
                        actionLabel={t('folders.manageImplants.undo')}
                        onAction={onUndoPending}
                    />
                ))}
                {isEmpty && (
                    <li className="py-4 text-xs text-subtle-foreground">
                        {t('folders.manageImplants.pickFromCatalog')}
                    </li>
                )}
            </ul>
        </aside>
    );
};
